import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getUserGymId } from "@/utils/gymAuth";
import {
  importSubscriptionTypes,
  getSubscriptionTypes,
} from "@/services/edgeFunctionWrappers/subscriptionTypesWrapper";
import { SubscriptionType, SubscriptionTypeImportData } from "@/types/subscriptionType";
import * as XLSX from "xlsx";
import { useCurrency } from "@/contexts/CurrencyContext";

interface ImportSubscriptionTypesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

interface ParsedType {
  name: string;
  sessions_total: number;
  /** `null` = le fichier ne fournit pas de durée de validité pour cette ligne. */
  validity_days: number | null;
  price: number;
  /** `null` = le fichier ne fournit pas de jokers pour cette ligne. */
  jokers_total: number | null;
}

export function ImportSubscriptionTypesDialog({
  open,
  onOpenChange,
  onSuccess,
}: ImportSubscriptionTypesDialogProps) {
  const { t } = useTranslation(["subscriptions", "bookingui"]);
  const { currency } = useCurrency();
  const gymId = getUserGymId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedTypes, setParsedTypes] = useState<ParsedType[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [existingTypes, setExistingTypes] = useState<SubscriptionType[]>([]);

  // Chargés à l'ouverture pour prévenir l'utilisateur de ce que l'import va
  // écraser côté serveur (voir l'alerte plus bas).
  useEffect(() => {
    if (!open || !gymId) return;
    let cancelled = false;
    getSubscriptionTypes(gymId, true)
      .then((types) => { if (!cancelled) setExistingTypes(types); })
      .catch((error) => {
        console.error("[ImportSubscriptionTypes] lecture des types existants:", error);
        if (!cancelled) setExistingTypes([]);
      });
    return () => { cancelled = true; };
  }, [open, gymId]);

  const existingByName = useMemo(
    () => new Map(existingTypes.map((type) => [type.name.trim().toLowerCase(), type])),
    [existingTypes]
  );

  // La fonction edge force `is_active: true` sur tout pack mis à jour : un pack
  // désactivé redevient visible sans que rien ne le signale. On ne peut pas
  // l'empêcher côté client, on le dit avant l'import.
  const typesToReactivate = useMemo(
    () => parsedTypes
      .map((t) => existingByName.get(t.name.trim().toLowerCase()))
      .filter((type): type is SubscriptionType => !!type && !type.is_active)
      .map((type) => type.name),
    [parsedTypes, existingByName]
  );

  const parseFile = useCallback(async (file: File) => {
    setIsProcessing(true);
    setParseError(null);
    setParsedTypes([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      if (jsonData.length < 2) {
        throw new Error(t("importSubscriptionTypes.noDataRow", { ns: "bookingui" }));
      }

      const headers = (jsonData[0] as string[]).map((h) =>
        String(h || "").toLowerCase().trim()
      );

      // Find column indices
      const nameIndex = headers.findIndex((h) =>
        ["nom", "name", "pack", "type", "formule"].includes(h)
      );
      const sessionsIndex = headers.findIndex((h) =>
        ["séances", "seances", "sessions", "nb séances", "nb seances"].includes(h)
      );
      const priceIndex = headers.findIndex((h) =>
        ["prix", "price", "tarif", "montant"].includes(h)
      );
      const validityIndex = headers.findIndex((h) =>
        ["validité", "validite", "validity", "durée", "duree", "jours"].includes(h)
      );
      const jokersIndex = headers.findIndex((h) =>
        ["jokers", "joker", "bonus"].includes(h)
      );

      if (nameIndex === -1) {
        throw new Error(t("importSubscriptionTypes.nameColumnNotFound", { ns: "bookingui" }));
      }
      if (sessionsIndex === -1) {
        throw new Error(t("importSubscriptionTypes.sessionsColumnNotFound", { ns: "bookingui" }));
      }
      if (priceIndex === -1) {
        throw new Error(t("importSubscriptionTypes.priceColumnNotFound", { ns: "bookingui" }));
      }

      const types: ParsedType[] = [];

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || !row[nameIndex]) continue;

        const name = String(row[nameIndex]).trim();
        const sessions = parseInt(String(row[sessionsIndex] || "1").replace(/\D/g, "")) || 1;
        const priceStr = String(row[priceIndex] || "0").replace(/[^\d.,]/g, "").replace(",", ".");
        const price = parseFloat(priceStr) || 0;
        const validity = validityIndex >= 0 && row[validityIndex]
          ? parseInt(String(row[validityIndex]).replace(/\D/g, "")) || null
          : null;
        // `null` (et non 0) quand la colonne est absente ou la cellule vide :
        // 0 est une valeur, l'absence n'en est pas une. Voir handleImport.
        const jokers = jokersIndex >= 0 && row[jokersIndex] !== undefined && row[jokersIndex] !== ""
          ? parseInt(String(row[jokersIndex]).replace(/\D/g, "")) || 0
          : null;

        if (name && sessions > 0 && price >= 0) {
          types.push({
            name,
            sessions_total: sessions,
            validity_days: validity,
            price,
            jokers_total: jokers,
          });
        }
      }

      if (types.length === 0) {
        throw new Error(t("importSubscriptionTypes.noValidTypesFound", { ns: "bookingui" }));
      }

      setParsedTypes(types);
    } catch (error: any) {
      setParseError(error.message);
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      parseFile(file);
    }
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleImport = async () => {
    if (!gymId || parsedTypes.length === 0) return;

    setIsProcessing(true);
    try {
      // Le serveur écrase `validity_days` et `jokers_total` sur un pack
      // existant avec ce qu'on lui envoie (`jokers_total: … || 0`). Un fichier
      // sans ces colonnes remettait donc tous les packs à « sans validité,
      // sans joker ». On relit les types existants et on renvoie leur valeur
      // actuelle pour toute donnée que le fichier ne fournit pas : la mise à
      // jour devient neutre sur ces colonnes.
      // Relecture fraîche au moment de l'import (l'état chargé à l'ouverture
      // ne sert qu'à l'aperçu).
      const currentTypes = await getSubscriptionTypes(gymId, true);
      const currentByName = new Map(
        currentTypes.map((type) => [type.name.trim().toLowerCase(), type])
      );

      const typesToImport: SubscriptionTypeImportData[] = parsedTypes.map((t) => {
        const existing = currentByName.get(t.name.trim().toLowerCase());
        return {
          name: t.name,
          sessions_total: t.sessions_total,
          validity_days: t.validity_days ?? existing?.validity_days ?? null,
          price: t.price,
          jokers_total: t.jokers_total ?? existing?.jokers_total ?? 0,
        };
      });

      const results = await importSubscriptionTypes(gymId, typesToImport);

      if (results.errors.length > 0) {
        toast.warning(
          t("importSubscriptionTypes.partialImport", {
            ns: "bookingui",
            created: results.created,
            updated: results.updated,
            errors: results.errors.length,
          })
        );
      } else {
        toast.success(
          t("importSubscriptionTypes.importSuccess", {
            ns: "bookingui",
            created: results.created,
            updated: results.updated,
          })
        );
      }

      onSuccess();
    } catch (error: any) {
      toast.error(error.message || t("types.importError"));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setParsedTypes([]);
    setParseError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("types.importTitle")}</DialogTitle>
          <DialogDescription>{t("types.importDescription")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File input */}
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-2">
              <FileSpreadsheet className="h-10 w-10 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t("types.importDropzone")}
              </span>
              <Button variant="outline" size="sm" type="button" onClick={handleSelectFile}>
                <Upload className="h-4 w-4 mr-2" />
                {t("types.selectFile")}
              </Button>
            </div>
          </div>

          {/* Parse error */}
          {parseError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {parsedTypes.length > 0 && (
            <div className="space-y-3">
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-500" />
                <AlertDescription>
                  {t("types.importPreview", { count: parsedTypes.length })}
                </AlertDescription>
              </Alert>

              {typesToReactivate.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {t("importTypes.reactivateWarning", { ns: "bookingui", count: typesToReactivate.length, names: typesToReactivate.join(", ") })}
                  </AlertDescription>
                </Alert>
              )}

              <div className="max-h-[200px] overflow-y-auto rounded border">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2">{t("types.name")}</th>
                      <th className="text-center p-2">{t("types.sessions")}</th>
                      <th className="text-center p-2">{t("types.validity")}</th>
                      <th className="text-right p-2">{t("types.price")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedTypes.map((type, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-2">{type.name}</td>
                        <td className="text-center p-2">{type.sessions_total}</td>
                        <td className="text-center p-2">
                          {type.validity_days
                            ? `${type.validity_days}${t("importSubscriptionTypes.daysSuffix", { ns: "bookingui" })}`
                            : existingByName.has(type.name.trim().toLowerCase())
                              ? t("importTypes.unchangedLabel", { ns: "bookingui" })
                              : "-"}
                        </td>
                        <td className="text-right p-2">{type.price} {currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              {t("types.cancel")}
            </Button>
            <Button
              onClick={handleImport}
              disabled={isProcessing || parsedTypes.length === 0}
            >
              {isProcessing ? t("types.importing") : t("types.confirmImport")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
